import { NumberRatingSelectField, TextField } from "@dgs/core";
import { useFormikContext } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventDetailResource, EventOffboardingFormValues } from "../../types/event";
import { EventFormColumns } from "../save/EventFormColumns";
import { EventOffboardingFormCard } from "./EventOffboardingFormCard";
import { EventOffboardingOverviewCard } from "./EventOffboardingOverviewCard";
import { isKeyMetricsStepValid } from "./EventOffboardingStepper";
import { useSubmitOffboardingStep } from "./eventOffboardingStepperUtils";

interface Props {
	event: EventDetailResource;
	variant: "outlined" | "elevated";
}

const RatingWrapper = styled.div`
	grid-column: span 2;
`;

export const EventOffboardingKeyMetrics: FC<Props> = ({ event, variant }) => {
	const { t } = useTranslation();
	const { setTouched, touched } = useFormikContext<EventOffboardingFormValues>();
	const submitStep = useSubmitOffboardingStep();

	const handleNextClick = useCallback(async () => {
		const errors = await setTouched({
			...touched,
			visitorsMetric: true,
			invitedPersonsMetric: true,
			registrationsMetric: true,
			customersMetric: true,
			employeesMetric: true,
			eventReachMetric: true,
			eventGoalRatingMetric: true,
		});

		if (errors && isKeyMetricsStepValid(errors)) {
			await submitStep(event.eventType, "key-metrics");
		}
	}, [event.eventType, setTouched, submitStep, touched]);

	return (
		<>
			<EventOffboardingOverviewCard event={event} variant={variant} />
			<EventOffboardingFormCard
				title={t("Key Metrics")}
				variant={variant}
				onSubmitClick={handleNextClick}
				submitButtonTitle={t("Next")}
				backButtonTitle={t("Back to event detail page")}
				prevLink="../../.."
			>
				<EventFormColumns>
					{event.eventType.visitorsMetricRequirement !== "prohibited" && (
						<TextField
							min="0"
							type="number"
							label={t("Total number of event visitors")}
							name="visitorsMetric"
							required={event.eventType.visitorsMetricRequirement === "required"}
						/>
					)}
					{event.eventType.invitedPersonsMetricRequirement !== "prohibited" && (
						<TextField
							min="0"
							type="number"
							label={t("Invited persons")}
							name="invitedPersonsMetric"
							required={event.eventType.invitedPersonsMetricRequirement === "required"}
						/>
					)}
					{event.eventType.registrationsMetricRequirement !== "prohibited" && (
						<TextField
							min="0"
							type="number"
							label={t("Registrations")}
							name="registrationsMetric"
							required={event.eventType.registrationsMetricRequirement === "required"}
						/>
					)}
					{event.eventType.customersMetricRequirement !== "prohibited" && (
						<TextField
							min="0"
							type="number"
							label={t("Customers on site")}
							name="customersMetric"
							required={event.eventType.customersMetricRequirement === "required"}
						/>
					)}
					{event.eventType.employeesMetricRequirement !== "prohibited" && (
						<TextField
							min="0"
							type="number"
							label={t("Employees on site")}
							name="employeesMetric"
							required={event.eventType.employeesMetricRequirement === "required"}
						/>
					)}
					{event.eventType.eventReachMetricRequirement !== "prohibited" && (
						<TextField
							min="0"
							type="number"
							label={t("Event reach")}
							name="eventReachMetric"
							required={event.eventType.eventReachMetricRequirement === "required"}
						/>
					)}
					{event.eventType.eventGoalRatingMetricRequirement !== "prohibited" && (
						<RatingWrapper>
							<NumberRatingSelectField
								label={t("Has the event goal been achieved?")}
								name="eventGoalRatingMetric"
								required={event.eventType.eventGoalRatingMetricRequirement === "required"}
								scaleStart={1}
							/>
						</RatingWrapper>
					)}
				</EventFormColumns>
			</EventOffboardingFormCard>
		</>
	);
};
